/* eslint-disable */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { isMobile } from 'react-device-detect'
import { 
  getCookie, 
  setCookie, 
  getOptanonConsentMap, 
  getOptanonConsentGroupMap, 
  convertOptanonMapToConsentObject,
  isOptanonConsentLatest
} from '@apps/utils/common'
import { 
  getShortTextFromPage, 
  getLongTextFromPage, 
  getCookieBodyContent,
  getProperty,
  getBySlug,
  getById
} from '@apps/utils/contentful'
import Sites from '@sites/index'
import envConfig from '@platformConfig/index'
import { useLocation } from 'react-router'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { ANALYTICS_COOKIE_CONSTANTS, ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import useTagManagerInfo from '../../_engine/hooks/useTagManagerInfo'

// Constants - exported for use in other components
export const ACKNOWLEDGE_COOKIE_NAME = 'cookieAcknowledge2'
const COOKIE_ACKNOWLEDGE_LAST_UPDATE = 'cookieAcknowledgeLastUpdate'
//  retrieved from Contentful, fall back to the value defined here  1 year in seconds (fallback)
const DEFAULT_COOKIE_EXPIRY_TIME = '31536000'
// Optanon group mapping
const OPTANON_GROUP_CODE_MAP = {
  C0001: ['security_storage'], // Essential
  C0002: ['analytics_storage'], // Performance & Analytics  
  C0003: ['functionality_storage'], // Functional
  C0004: ['personalization_storage', 'ad_storage', 'ad_user_data', 'ad_personalization'] // Targeting & Ads
}

export const useCookieConsent = ({ 
  showBanner = true, 
  language, 
  province, 
  userType 
} = {}) => {
  // States
  const [showCookieModal, setShowCookieModal] = useState(false)
  const [showCookieConsentBanner, setShowCookieConsentBanner] = useState(false)
  const [cookieConsentObject, setCookieConsentObject] = useState({})
  const [cookieControllers, setCookieControllers] = useState([])
  const [originalCookieControllers, setOriginalCookieControllers] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasInitialGtmDefault, setHasInitialGtmDefault] = useState(false)
  // const previousProvinceRef = useRef(null)
  const currentProvinceRef = useRef(province)
  const isFirstRender = useRef(true)
  
  // Update province ref when province changes
  useEffect(() => {
    currentProvinceRef.current = province
  }, [province])
  
  // Get current location for page navigation detection
  const location = useLocation()
  const previousPathnameRef = useRef(location.pathname) // Initialize with current pathname

  // Get cookieContent from Redux
  const { cookieContent } = useSelector((state) => state.common.content)
  
  // Get current language data
  const currentLangData = useMemo(() => {
    return cookieContent?.[language] || cookieContent?.en || {}
  }, [cookieContent, language])

  // Get cookie expiration time from Contentful or use default
  const cookieExpiryTime = useMemo(() => {
    const expirationTime = currentLangData?.cookieExpirationTime
    return expirationTime ? String(expirationTime) : DEFAULT_COOKIE_EXPIRY_TIME
  }, [currentLangData])

  // Cookie regions
  const cookieRegions = useMemo(() => {
    return currentLangData?.activeRegions?.map(r => r.toUpperCase()) || []
  }, [currentLangData])

  const tagManagerInfo = useTagManagerInfo()

  // eslint-disable-next-line camelcase
  const pushClickAnalytics = ({ click_text, event_type, eventCategory, event_click = ANALYTICS_EVENT_TYPES.CLICK, consentObject } = {}) => {
    if (userType !== 'consumer') {
      return
    }
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      setPageAnalytics(location, 'click', province, language, {
        ...tagManagerInfo,
        click_text,
        eventCategory,
        consentObject
      }, event_type,event_click)
    )
  }

  // Get default consent based on province
  const getDefaultConsent = (prov) => {
    const provLower = prov?.toLowerCase()
    if (provLower === 'qc') {
      try {
        return JSON.parse(currentLangData?.quebecDefaultCookieConsentValues || '{}')
      } catch {
        return {}
      }
    }
    try {
      return JSON.parse(currentLangData?.restOfCanadaDefaultCookieConsentValues || '{}')
    } catch {
      return {}
    }
  }

  // GTM consent function
  const setGtmCookieConsent = async (updatedConsentObj, eventType) => {
    try {
      // Only send GTM consent for consumer users
      if (userType !== Sites.consumer) {
        return
      }
      if (typeof window === 'undefined') {
        return
      }
      function gtmPromise() {
        window.dataLayer.push(arguments)
      }
      gtmPromise('consent', eventType, updatedConsentObj)?.then?.().catch?.()
    } catch (error) {
      console.error('setGtmCookieConsent error', error)
    }
  }

  // Capture consent timestamp
  const captureConsentTimestamp = (expiryTime, forceCurrentTime = false) => {
    const optanonMap = getOptanonConsentMap()
    let timestamp
    
    // Use current time if forced or if no Optanon datestamp exists
    if (forceCurrentTime || !optanonMap?.datestamp) {
      timestamp = new Date().toISOString()
    } else {
      timestamp = decodeURIComponent(optanonMap.datestamp)
    }
    
    setCookie(
      COOKIE_ACKNOWLEDGE_LAST_UPDATE, 
      timestamp, 
      expiryTime || cookieExpiryTime, 
      envConfig.locale.cookieDomain, 
      false
    )
    
    return timestamp
  }

  // Update cookie consent
  const updateCookieConsent = (cookieObject, hideBanner = true, updateTimestamp = true, forceCurrentTime = false) => {
    const consentObjString = JSON.stringify(cookieObject)
    
    setCookie(
      ACKNOWLEDGE_COOKIE_NAME,
      consentObjString,
      cookieExpiryTime,
      envConfig.locale.cookieDomain
    )
    
    if (updateTimestamp) {
      captureConsentTimestamp(cookieExpiryTime, forceCurrentTime)
    }
    
    if (hideBanner) {
      setShowCookieConsentBanner(false)
    }
    setShowCookieModal(false)
    
    // Dispatch a custom event to notify other instances
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
        detail: { cookieObject, hideBanner } 
      }))
    }
  }

  // Handle cookie submission
  const handleCookieSubmission = (consentObject = null, consentChanged = false) => {
    let enhancedCookieObject = consentObject
    
    if (!enhancedCookieObject) {
      enhancedCookieObject = {}
      cookieControllers.forEach((item) => {
        item?.target?.forEach((cookieKey) =>
          item?.granted
            ? (enhancedCookieObject[cookieKey] = 'granted')
            : (enhancedCookieObject[cookieKey] = 'denied')
        )
      })
    }
    
    // Force current time when updating from modal
    updateCookieConsent(enhancedCookieObject, true, true, true)
   
    if (consentChanged) {
      setGtmCookieConsent(enhancedCookieObject, 'update')
    }

    pushClickAnalytics({ click_text: getShortTextFromPage(currentLangContent, 'cookieConfirmButton'), event_type: ANALYTICS_COOKIE_CONSTANTS.CONSENT_CHOICE, eventCategory: ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL,  consentObject: enhancedCookieObject })
    if (consentChanged) {
      console.log('Cookie consent changed, triggering GTM')
      setGtmCookieConsent(enhancedCookieObject, 'update')
    }
  }

  // Handle cookie state
  const handleCookieState = (cookieValue) => {
    if (!cookieValue) return
    
    if (showCookieModal) {
      return
    }
    
    try {
      const parsedConsent = typeof cookieValue === 'string' ? JSON.parse(cookieValue) : cookieValue
      
      setCookieControllers(prevControllers => {
        if (!prevControllers || prevControllers.length === 0) {
          return prevControllers
        }
        
        return prevControllers.map(controller => {
          const hasConsent = controller.target?.some(cookieKey => 
            parsedConsent[cookieKey] === 'granted'
          )
          return {
            ...controller,
            granted: controller.required || hasConsent || false
          }
        })
      })
    } catch (e) {
      console.error('Error parsing existing cookie consent:', e)
    }
  }

  // Parse current language content
  const currentLangContent = useMemo(() => {
    if (currentLangData?.header || currentLangData?.body || currentLangData?.mobileBody) {
      // Get modal content data using the same pattern as CookieModal
      const modalContentData = getBySlug(currentLangData?.modalContent, '_modal_content_cookie_view')
      const essentialAccordion = getById(modalContentData?.accordions, 'essential')
      const confirmButton = getById(modalContentData?.leftButtons, 'button_modal_confirm_choices')
      const manageButton = getById(currentLangData?.buttons, 'consent_manage')
      const acceptButton = getById(currentLangData?.buttons, 'consent_all')
      
      return {
        shortTextFields: [
          { slug: 'consentBannerHeading', value: getProperty(currentLangData, 'header') },
          { slug: 'manageCookieButton', value: getProperty(manageButton, 'buttonLabel') },
          { slug: 'acceptCookieButton', value: getProperty(acceptButton, 'buttonLabel') },
          { slug: 'cookieModalHeading', value: getProperty(modalContentData, 'header') },
          { slug: 'cookieConfirmButton', value: getProperty(confirmButton, 'buttonLabel') },
          { slug: 'alwaysOnLabel', value: getProperty(essentialAccordion?.toggle, 'label', 'Always On') },
          { slug: 'closeCookieBannerAriaLabel', value: 'Close cookie banner' },
          { slug: 'closeModalAriaLabel', value: 'Close modal' }
        ],
        longTextFields: [
          { slug: 'consentBannerBody', value: getCookieBodyContent(currentLangData, isMobile) },
          { slug: 'cookieModalDescription', value: getProperty(modalContentData, 'text') }
        ],
        images: {
          closeIcon: {
            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE4IDZMNiAxOCIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNNiA2TDE4IDE4IiBzdHJva2U9IiMwMDAiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPg=='
          }
        },
        cookieCategories: modalContentData?.accordions?.map(acc => ({
          id: acc.id,
          title: acc.title,
          description: acc.content,
          required: acc.id === 'essential',
          target: acc.cookieConsentKeys || (acc.id === 'essential' ? ['security_storage'] : [])
        })) || []
      }
    }
    return {}
  }, [currentLangData])

  // Initialize cookie consent object and controllers
  useEffect(() => {
    if (cookieContent) {
      setIsInitialized(true)
      // Set cookieConsentObject from cookieContent if it's passed directly
      if (currentLangData && Object.keys(currentLangData).length > 0) {
        // Ensure all necessary fields including buttons are included
        const consentObject = {
          ...currentLangData,
          buttons: currentLangData.buttons || [],
          header: currentLangData.header || '',
          body: getCookieBodyContent(currentLangData, isMobile)
        }
        setCookieConsentObject(consentObject)
        
        // Also set up cookie controllers from modalContent accordions
        const modalContentData = getBySlug(currentLangData?.modalContent, '_modal_content_cookie_view')
        if (modalContentData?.accordions) {
          const mockCategories = modalContentData.accordions
          const defaultControllers = mockCategories.map(category => {
            const isGrantedByDefault = category.id === 'essential'
            return {
              ...category,
              granted: isGrantedByDefault,
              target: category.cookieConsentKeys || (category.id === 'essential' ? ['security_storage'] : [])
            }
          })
          setCookieControllers(defaultControllers)
          setOriginalCookieControllers([...defaultControllers])
        }
        return
      }
    }
    
    if (currentLangContent?.cookieCategories && currentLangContent.cookieCategories.length > 0) {
      setCookieConsentObject({
        shortText: {
          consentBannerHeading: getShortTextFromPage(currentLangContent, 'consentBannerHeading'),
          manageCookieButton: getShortTextFromPage(currentLangContent, 'manageCookieButton'),
          acceptCookieButton: getShortTextFromPage(currentLangContent, 'acceptCookieButton'),
          cookieModalHeading: getShortTextFromPage(currentLangContent, 'cookieModalHeading'),
          cookieConfirmButton: getShortTextFromPage(currentLangContent, 'cookieConfirmButton'),
          alwaysOnLabel: getShortTextFromPage(currentLangContent, 'alwaysOnLabel'),
          closeModalAriaLabel: 'Close modal',
          closeCookieBannerAriaLabel: 'Close cookie banner'
        },
        longText: {
          consentBannerBody: getLongTextFromPage(currentLangContent, 'consentBannerBody'),
          cookieModalDescription: getLongTextFromPage(currentLangContent, 'cookieModalDescription')
        },
        images: currentLangContent?.images || {}
      })
      
      const cookieCategories = currentLangContent?.cookieCategories || []
      const defaultControllers = cookieCategories.map(category => {
        const isGrantedByDefault = category.id === 'essential'
        return {
          ...category,
          granted: isGrantedByDefault
        }
      })
      setCookieControllers(defaultControllers)
      setOriginalCookieControllers([...defaultControllers])
      setIsInitialized(true)
    } else if (cookieContent && currentLangData?.modalContent) {
      // Using data for cookie controllers
      const modalContentData = getBySlug(currentLangData?.modalContent, '_modal_content_cookie_view')
      const mockCategories = modalContentData?.accordions || []
      const defaultControllers = mockCategories.map(category => {
        const isGrantedByDefault = category.id === 'essential'
        return {
          ...category,
          granted: isGrantedByDefault
        }
      })
      setCookieControllers(defaultControllers)
      setOriginalCookieControllers([...defaultControllers])
    }
  }, [currentLangContent, language, cookieContent, currentLangData])

  // Update controllers when modal opens
  useEffect(() => {
    if (showCookieModal) {
      // Read the latest cookie value when modal opens
      const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
      const defaultConsent = getDefaultConsent(currentProvinceRef.current)
      
      let consentToUse = defaultConsent
      if (cookieValue) {
        try {
          consentToUse = JSON.parse(cookieValue)
        } catch (e) {
          console.error('Error parsing cookie when modal opens:', e)
          consentToUse = defaultConsent
        }
      }
      
      // Update controllers based on current consent
      const updatedControllers = cookieControllers.map(controller => {
        const hasConsent = controller.target?.some(cookieKey => 
          consentToUse[cookieKey] === 'granted'
        )
        return {
          ...controller,
          granted: controller.required || hasConsent || false
        }
      })
      
      setCookieControllers(updatedControllers)
      setOriginalCookieControllers([...updatedControllers])
    }
  }, [showCookieModal])

  // Listen for cookie consent updates from other instances
  useEffect(() => {
    const handleCookieConsentUpdate = (event) => {
      if (event.detail?.hideBanner) {
        setShowCookieConsentBanner(false)
      }
      
      // Always update controllers when cookie is updated
      const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
      if (cookieValue) {
        try {
          const parsedConsent = JSON.parse(cookieValue)
          
          // Update controllers to reflect current cookie state
          setCookieControllers(prevControllers => {
            if (!prevControllers || prevControllers.length === 0) {
              return prevControllers
            }
            
            return prevControllers.map(controller => {
              const hasConsent = controller.target?.some(cookieKey => 
                parsedConsent[cookieKey] === 'granted'
              )
              return {
                ...controller,
                granted: controller.required || hasConsent || false
              }
            })
          })
        } catch (e) {
          console.error('Error parsing cookie in cookieConsentUpdated event:', e)
        }
      }
    }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('cookieConsentUpdated', handleCookieConsentUpdate)
      return () => {
        window.removeEventListener('cookieConsentUpdated', handleCookieConsentUpdate)
      }
    }
  }, [])
  
  // Listen for province changes and update GTM consent
  useEffect(() => {
    const handleProvinceChange = (event) => {
      // Only execute for consumer users
      if (event.detail?.newProvince && userType === Sites.consumer) {
        const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
        
        // If cookie exists, don't call setGtmCookieConsent
        if (cookieValue) {
          return
        }
        
        // No cookie exists, use default consent for the new province
        const newProvince = event.detail.newProvince
        const provLower = newProvince?.toLowerCase()
        let consentToSend = {}
        
        if (provLower === 'qc') {
          try {
            consentToSend = JSON.parse(currentLangData?.quebecDefaultCookieConsentValues || '{}')
          } catch {
            consentToSend = {}
          }
        } else {
          try {
            consentToSend = JSON.parse(currentLangData?.restOfCanadaDefaultCookieConsentValues || '{}')
          } catch {
            consentToSend = {}
          }
        }
        
        // No cookie, so use 'default'
        setGtmCookieConsent(consentToSend, 'default')
      }
    }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('provinceChanged', handleProvinceChange)
      return () => {
        window.removeEventListener('provinceChanged', handleProvinceChange)
      }
    }
  }, [userType, currentLangData])
  
  // Handle page navigation - set GTM consent on each page change (not on initial load)
  useEffect(() => {
    // Skip the very first render
    if (isFirstRender.current) {
      isFirstRender.current = false
      previousPathnameRef.current = location.pathname
      return
    }
    
    // Only run for consumer users
    if (userType !== Sites.consumer) {
      return
    }
    
    // Check if pathname actually changed
    if (previousPathnameRef.current === location.pathname) {
      return // No actual navigation happened
    }
    
    // Check if this is just a province change in the URL
    // URL pattern: /en/{province}/prepaid/...
    const previousParts = previousPathnameRef.current?.split('/')
    const currentParts = location.pathname?.split('/')
    
    // If only the province part (index 2) changed, it's a province change, not navigation
    if (previousParts && currentParts && 
        previousParts.length === currentParts.length &&
        (previousParts[1] !== currentParts[1] || // same language
        previousParts[2] !== currentParts[2]) && // different province
        previousParts.slice(3).join('/') === currentParts.slice(3).join('/')) { // same rest of path
      previousPathnameRef.current = location.pathname
      return
    }
    
    // Update the previous pathname
    previousPathnameRef.current = location.pathname
    
    const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
    let consentToSend = {}
    
    if (cookieValue) {
      // If cookie exists, use the cookie values
      try {
        consentToSend = JSON.parse(cookieValue)
      } catch (e) {
        console.error('Failed to parse cookie on navigation:', e)
        // Fallback to default consent
        consentToSend = getDefaultConsent(province)
      }
    } else {
      // No cookie, use default consent for current province
      consentToSend = getDefaultConsent(province)
    }
    
    // Always use 'default' for page navigation
    setGtmCookieConsent(consentToSend, 'default')
  }, [location.pathname, userType, province]) // Add necessary dependencies

  // Cookie consent logic with Optanon synchronization
  useEffect(() => {
    const currentProvince = currentProvinceRef.current
    
    if (!isInitialized || showCookieModal || !currentProvince || !ACKNOWLEDGE_COOKIE_NAME) {
      return
    }

    const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
    const optanonMap = getOptanonConsentGroupMap() ?? {}
    const hasCookieConsent = !!cookieValue
    const activeRegions = cookieRegions.map((region) => region.trim().toUpperCase())
    const userProvince = currentProvince.toUpperCase()
    const regionActive = activeRegions.includes(userProvince)

    if (!regionActive) {
      setShowCookieConsentBanner(false)
      return
    }

    // No Optanon and no cookies - use default consent
    if (!Object.keys(optanonMap).length && regionActive && !cookieValue) {
      const defaultConsent = getDefaultConsent(currentProvince)
      // Only call GTM on initial load
      if (!hasInitialGtmDefault) {
        setGtmCookieConsent(defaultConsent, 'default')
        setHasInitialGtmDefault(true)
      }
      
      if (!showCookieModal) {
        setCookieControllers(prevControllers => {
          if (!prevControllers || prevControllers.length === 0) return prevControllers
          
          return prevControllers.map(category => {
            const hasDefaultConsent = category.target?.some(cookieKey => 
              defaultConsent[cookieKey] === 'granted'
            )
            return {
              ...category,
              granted: category.required || hasDefaultConsent || false
            }
          })
        })
      }
      
      if (showBanner) {
        setShowCookieConsentBanner(true)
      } else {
        console.log('🍪 showBanner is false, not showing banner')
      }
      return
    }

    // Optanon exists and no cookies - create from Optanon
    if (Object.keys(optanonMap).length && regionActive && !cookieValue) {
      const optanonConsent = convertOptanonMapToConsentObject(optanonMap, OPTANON_GROUP_CODE_MAP)
      updateCookieConsent(optanonConsent, false, true, false) // Use Optanon's timestamp, not current time
      if (!hasInitialGtmDefault) {
        setGtmCookieConsent(optanonConsent, 'default')
        setHasInitialGtmDefault(true)
      }
      
      if (!showCookieModal) {
        setCookieControllers(prevControllers => {
          if (!prevControllers || prevControllers.length === 0) return prevControllers
          
          return prevControllers.map(category => {
            const hasOptanonConsent = category.target?.some(cookieKey => 
              optanonConsent[cookieKey] === 'granted'
            )
            return {
              ...category,
              granted: category.required || hasOptanonConsent || false
            }
          })
        })
      }
      
      setShowCookieConsentBanner(false)
      return
    }

    // Show banner only if no cookie exists
    if (regionActive && !hasCookieConsent && showBanner) {
      setShowCookieConsentBanner(true)
      if (cookieValue) {
        handleCookieState(cookieValue)
      }
      return
    }

    // User has cookie - don't show banner but send default consent to GTM
    if (regionActive && hasCookieConsent) {
      // Check if OptanonConsent is newer than cookieAcknowledgeLastUpdate
      if (Object.keys(optanonMap).length && isOptanonConsentLatest(COOKIE_ACKNOWLEDGE_LAST_UPDATE)) {
        // OptanonConsent is newer, update cookieAcknowledge2 with Optanon values
        const optanonConsent = convertOptanonMapToConsentObject(optanonMap, OPTANON_GROUP_CODE_MAP)
        updateCookieConsent(optanonConsent, false, true, false) // Use Optanon's timestamp
        handleCookieState(optanonConsent)
        
        // Send updated consent to GTM
        if (!hasInitialGtmDefault) {
          setGtmCookieConsent(optanonConsent, 'default')
          setHasInitialGtmDefault(true)
        }
      } else {
        handleCookieState(cookieValue)
        
        // Parse existing cookie and send to GTM as default on initial page load only
        if (!hasInitialGtmDefault) {
          try {
            const existingConsent = JSON.parse(cookieValue)
            setGtmCookieConsent(existingConsent, 'default')
            setHasInitialGtmDefault(true)
          } catch (e) {
            console.error('Failed to parse existing cookie for GTM:', e)
          }
        }
      }
      
      setShowCookieConsentBanner(false)
      return
    }

    // Handle existing cookie state
    if (cookieValue) {
      handleCookieState(cookieValue)
    }
  }, [isInitialized, showCookieModal, cookieRegions, showBanner]) // Remove province from dependencies

  // Handler functions
  const handleManageCookies = () => {
    // Analytics
    const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
    let enhancedCookieObject = {}
    
    if (cookieValue) {
      try {
        enhancedCookieObject = JSON.parse(cookieValue)
      } catch (e) {
        console.error('Error parsing cookie for analytics:', e)
        enhancedCookieObject = getDefaultConsent(province)
      }
    } else {
      enhancedCookieObject = getDefaultConsent(province)
    }

    pushClickAnalytics({ click_text: getShortTextFromPage(currentLangContent, 'manageCookieButton'), event_type: `${getShortTextFromPage(currentLangContent, 'manageCookieButton')} Clicked`, eventCategory: ANALYTICS_COOKIE_CONSTANTS.COOKIE_FOOTER, consentObject: enhancedCookieObject })
    
    // Open modal - the useEffect will handle updating controllers
    setShowCookieModal(true)
  }

  const handleAcceptAllCookies = () => {
    // In QC region, Accept All should maintain denied state for certain cookies
    const isQuebec = province?.toLowerCase() === 'qc'
    let enhancedCookieObject = {}
    
    if (isQuebec) {
      // For QC, use quebecDefaultCookieConsentValues
      const qcDefaultConsent = getDefaultConsent(province)
      
      // Update controllers - set according to QC default values
      const updatedControllers = cookieControllers.map(controller => {
        // Essential cookies are always true
        if (controller.required) {
          return { ...controller, granted: true }
        }
        
        // Check QC default values for other cookies
        const hasConsent = controller.target?.some(cookieKey => 
          qcDefaultConsent[cookieKey] === 'granted'
        )
        
        return {
          ...controller,
          granted: hasConsent || false
        }
      })
      
      setCookieControllers(updatedControllers)
      enhancedCookieObject = qcDefaultConsent
      
    } else {
      // Other regions approve all cookies
      const updatedControllers = cookieControllers.map(controller => ({
        ...controller,
        granted: true
      }))
      setCookieControllers(updatedControllers)
      
      updatedControllers.forEach((item) => {
        item?.target?.forEach((cookieKey) => {
          enhancedCookieObject[cookieKey] = 'granted'
        })
      })
    }
    
    // Force current time when accepting all cookies
    updateCookieConsent(enhancedCookieObject, true, true, true)
    setGtmCookieConsent(enhancedCookieObject, 'update')
    pushClickAnalytics({ click_text: getShortTextFromPage(currentLangContent, 'acceptCookieButton'), event_type: ANALYTICS_COOKIE_CONSTANTS.CONSENT_UPDATE, eventCategory: ANALYTICS_COOKIE_CONSTANTS.COOKIE_FOOTER, consentObject: enhancedCookieObject })
  }

  const handleCloseBannerClick = () => {
    const defaultConsent = getDefaultConsent(province)
    updateCookieConsent(defaultConsent, true, true)    
    setGtmCookieConsent(defaultConsent, 'update') // Changed to 'update' for X button
    pushClickAnalytics({ click_text: ANALYTICS_COOKIE_CONSTANTS.CLOSE_BANNER, event_type: `${ANALYTICS_COOKIE_CONSTANTS.CLOSE_BANNER} Clicked`, eventCategory: ANALYTICS_COOKIE_CONSTANTS.COOKIE_BANNER, consentObject: defaultConsent })
    setShowCookieConsentBanner(false)
  }

  const handleCloseModalClick = () => {
    setCookieControllers(originalCookieControllers)
    setShowCookieModal(false)
    
    // When closing modal, send current cookie state to GTM
    const cookieValue = getCookie(ACKNOWLEDGE_COOKIE_NAME)
    if (cookieValue) {
      try {
        const consent = JSON.parse(cookieValue)
        // setGtmCookieConsent(consent, 'update')
        pushClickAnalytics({ click_text: ANALYTICS_COOKIE_CONSTANTS.CLOSE_MODAL, event_type: `${ANALYTICS_COOKIE_CONSTANTS.CLOSE_MODAL} Clicked`, eventCategory: ANALYTICS_COOKIE_CONSTANTS.COOKIE_MODAL, consentObject: consent })
      } catch (e) {
        console.error('Failed to update GTM on modal close:', e)
      }
    }
  }

  const handleToggle = (e, itemId) => {
    setCookieControllers(prev => {
      return prev.map(controller => 
        controller.id === itemId 
          ? { ...controller, granted: !controller.granted }
          : controller
      )
    })
  }

  const onModalSubmit = () => {
    handleCookieSubmission(null, true)  // true to trigger GTM update
  }

  // Calculate if region is active for current province
  const isRegionActive = () => {
    const activeRegions = cookieRegions.map((r) => r.trim().toUpperCase())
    const userProvince = province?.toUpperCase()
    return activeRegions.includes(userProvince)
  }

  return {
    // States
    showCookieModal,
    setShowCookieModal,
    showCookieConsentBanner,
    setShowCookieConsentBanner,
    cookieConsentObject,
    cookieControllers,
    setCookieControllers,
    
    // Handlers
    handleManageCookies,
    handleAcceptAllCookies,
    handleCloseBannerClick,
    handleCloseModalClick,
    handleToggle,
    onModalSubmit,
    handleCookieSubmission,
    
    // Utils
    getDefaultConsent,
    setGtmCookieConsent,
    isRegionActive
  }
}